import React from "react";
import PropTypes from "prop-types";
import { AlertTriangle, Loader2, Play, CheckCircle, XCircle, RefreshCw } from "lucide-react";

/*
 * Temporary banner component — will evolve as the pipeline states are refined.
 * Current states handled: analyzing, awaiting_review, generating_script,
 * regenerating_script, ready_to_execute, executing, succeeded, failed.
 */

const TOOL_BY_TARGET = {
  gcp:    "Terraform",
  oci_vm: "Ansible",
};

const CONFIG = {
  created: {
    border: "border-slate-200 bg-slate-50",
    bar:    "bg-slate-400",
    Icon:   Loader2,
    iconCls:"text-slate-400",
    spin:   false,
    title:  "Creating pipeline…",
    subtitle: "Run is queued and will start shortly.",
  },
  analyzing: {
    border: "border-blue-200 bg-blue-50",
    bar:    "bg-blue-500",
    Icon:   Loader2,
    iconCls:"text-blue-500",
    spin:   true,
    title:  "Creating pipeline…",
    subtitle: "The AI is cloning the repo and analysing your code.",
  },
  awaiting_review: {
    border: "border-orange-200 bg-orange-50",
    bar:    "bg-orange-500",
    Icon:   AlertTriangle,
    iconCls:"text-orange-500",
    spin:   false,
    title:  "Pipeline paused for your review",
    subtitle: (count, target) =>
      `The AI has proposed ${count} ${target ? target.toUpperCase() : ""} services based on the codebase. Approve to continue, edit to refine, or reject to re-run analysis with feedback.`,
  },
  generating_script: {
    border: "border-violet-200 bg-violet-50",
    bar:    "bg-violet-500",
    Icon:   Loader2,
    iconCls:"text-violet-500",
    spin:   true,
    title:  "Generating deployment script…",
    subtitle: "The AI is converting the approved proposal into a runnable script.",
  },
  regenerating_script: {
    border: "border-violet-200 bg-violet-50",
    bar:    "bg-violet-500",
    Icon:   RefreshCw,
    iconCls:"text-violet-500",
    spin:   true,
    title:  "Re-analysing with your feedback…",
    subtitle: "The AI is incorporating your rejection notes and will produce a revised proposal.",
  },
  ready_to_execute: {
    border: "border-indigo-200 bg-indigo-50",
    bar:    "bg-indigo-500",
    Icon:   Play,
    iconCls:"text-indigo-600",
    spin:   false,
    title:  "Script generated. Spot-check it, then execute when ready.",
    subtitle: (_count, target) => {
      const tool = TOOL_BY_TARGET[target] ?? target;
      return `The ${tool} script below was generated from the approved proposal.`;
    },
  },
  failed: {
    border: "border-red-200 bg-red-50",
    bar:    "bg-red-500",
    Icon:   XCircle,
    iconCls:"text-red-500",
    spin:   false,
    title:  "Deployment failed",
    subtitle: (_count, _target, errorMessage) =>
      errorMessage || "One or more steps encountered an error. See the logs for details.",
  },
};

const FALLBACK = {
  border: "border-gray-200 bg-gray-50",
  bar:    "bg-gray-400",
  Icon:   Loader2,
  iconCls:"text-gray-400",
  spin:   false,
  title:  "Processing…",
  subtitle: "",
};

export default function PipelineStatusAlert({ status, serviceCount = 0, target = "", errorMessage = "" }) {
  const bannerConfig = CONFIG[status] ?? FALLBACK;
  const { Icon, spin, iconCls, border, bar, title, subtitle } = bannerConfig;

  const subtitleText = typeof subtitle === "function"
    ? subtitle(serviceCount, target, errorMessage)
    : subtitle;

  return (
    <div className={`flex gap-4 rounded-xl border p-4 ${border} ${status === "failed" && errorMessage ? "items-start" : "items-center"}`}>
      <div className={`w-1 self-stretch rounded-full shrink-0 ${bar}`} />
      <div className="shrink-0 mt-0.5">
        <Icon size={18} className={`${iconCls} ${spin ? "animate-spin" : ""}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        {status === "failed" && errorMessage ? (
          <>
            <p className="mt-0.5 text-sm text-slate-600">The pipeline stopped with the following error:</p>
            <pre className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-wrap break-all font-mono">
              {errorMessage}
            </pre>
          </>
        ) : (
          subtitleText && <p className="mt-0.5 text-sm text-slate-600">{subtitleText}</p>
        )}
      </div>
    </div>
  );
}

PipelineStatusAlert.propTypes = {
  status:       PropTypes.string.isRequired,
  serviceCount: PropTypes.number,
  target:       PropTypes.string,
  errorMessage: PropTypes.string,
};
